/**
 * useGlobalSearch Hook
 *
 * Provides unified search across multiple entity types:
 * - Flows
 * - Primitives
 * - Executions
 * - Edge Devices/Modems
 * - Locations
 * - Schedules
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getFlows } from '@/services/flowService';
import { getAllPrimitives } from '@/services/primitiveService';
import { edgeDeviceService } from '@/services/edgeDeviceService';
import { getLocations as getHierarchicalLocations, filterLocationsByTenancy } from '@/services/hierarchicalLocationService';
import { useRealtimeExecutions } from './useRealtimeExecutions';
import { Flow } from '@/types/firestore';
import { FlowDefinition, CentralEdgeDevice } from '@simcheck/shared-types';

export type SearchResultType =
  | 'flow'
  | 'primitive'
  | 'execution'
  | 'device'
  | 'modem'
  | 'location'
  | 'schedule';

// Default types - defined outside component to maintain stable reference
const DEFAULT_INCLUDE_TYPES: SearchResultType[] = ['flow', 'primitive', 'execution', 'device', 'modem', 'location', 'schedule'];

export interface SearchResult {
  id: string;
  type: SearchResultType;
  title: string;
  subtitle?: string;
  status?: string;
  url: string;
  keywords?: string[];
}

interface UseGlobalSearchOptions {
  /** Debounce delay in milliseconds (default: 300) */
  debounceMs?: number;
  /** Maximum results per category (default: 5) */
  maxPerCategory?: number;
  /** Entity types to include in search */
  includeTypes?: SearchResultType[];
}

interface UseGlobalSearchResult {
  query: string;
  setQuery: (query: string) => void;
  results: SearchResult[];
  resultsByType: Record<SearchResultType, SearchResult[]>;
  loading: boolean;
  error: string | null;
  totalCount: number;
}

/**
 * Hook for global search across all entity types
 */
export function useGlobalSearch(options: UseGlobalSearchOptions = {}): UseGlobalSearchResult {
  const {
    debounceMs = 300,
    maxPerCategory = 5,
    includeTypes: optionsIncludeTypes,
  } = options;

  // Use stable reference for includeTypes to prevent infinite loops
  const includeTypes = optionsIncludeTypes || DEFAULT_INCLUDE_TYPES;
  const includeTypesKey = includeTypes.join(',');

  const { activeOrganization, isSuperAdmin } = useAuth();
  const { executions } = useRealtimeExecutions({ limitCount: 20 });

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cached data for search
  const [flows, setFlows] = useState<Flow[]>([]);
  const [primitives, setPrimitives] = useState<FlowDefinition[]>([]);
  const [devices, setDevices] = useState<CentralEdgeDevice[]>([]);
  const [locations, setLocations] = useState<any[]>([]);

  // Debounce query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [query, debounceMs]);

  // Load initial data when organization changes
  useEffect(() => {
    const loadData = async () => {
      if (!activeOrganization?.id) return;

      setLoading(true);
      setError(null);

      try {
        // Load data in parallel
        // Legacy schedule search retired with the test_schedules collection
        // (scheduling now lives on /tests) — no schedule fetch here anymore.
        const [flowsData, primitivesData, devicesData, locationsData] = await Promise.allSettled([
          includeTypes.includes('flow') ? getFlows(activeOrganization.id) : Promise.resolve([]),
          includeTypes.includes('primitive') && isSuperAdmin
            ? getAllPrimitives()
            : Promise.resolve([]),
          includeTypes.includes('device') || includeTypes.includes('modem')
            ? edgeDeviceService.getDevices()
            : Promise.resolve([]),
          includeTypes.includes('location')
            ? getHierarchicalLocations()
            : Promise.resolve([]),
        ]);

        if (flowsData.status === 'fulfilled') setFlows(flowsData.value);
        if (primitivesData.status === 'fulfilled') setPrimitives(primitivesData.value);
        if (devicesData.status === 'fulfilled') setDevices(devicesData.value);
        if (locationsData.status === 'fulfilled') {
          // Tenancy (#202): never surface other orgs' private locations.
          setLocations(
            filterLocationsByTenancy(locationsData.value, activeOrganization?.id, isSuperAdmin)
          );
        }

      } catch (err) {
        console.error('Error loading search data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load search data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrganization?.id, isSuperAdmin, includeTypesKey]);

  // Perform search
  const searchResults = useMemo(() => {
    if (!debouncedQuery.trim()) {
      return { results: [], resultsByType: {} as Record<SearchResultType, SearchResult[]> };
    }

    const searchTerm = debouncedQuery.toLowerCase().trim();
    const allResults: SearchResult[] = [];
    const resultsByType: Record<SearchResultType, SearchResult[]> = {
      flow: [],
      primitive: [],
      execution: [],
      device: [],
      modem: [],
      location: [],
      schedule: [],
    };

    // Search flows
    if (includeTypes.includes('flow')) {
      const flowResults = flows
        .filter(flow =>
          flow.name.toLowerCase().includes(searchTerm) ||
          flow.description?.toLowerCase().includes(searchTerm) ||
          flow.tags?.some(tag => tag.toLowerCase().includes(searchTerm))
        )
        .slice(0, maxPerCategory)
        .map(flow => ({
          id: flow.id,
          type: 'flow' as SearchResultType,
          title: flow.name,
          subtitle: flow.description || `${flow.status} flow`,
          status: flow.status,
          url: `/flows/${flow.id}`,
          keywords: flow.tags,
        }));
      resultsByType.flow = flowResults;
      allResults.push(...flowResults);
    }

    // Search primitives
    if (includeTypes.includes('primitive')) {
      const primitiveResults = primitives
        .filter(p =>
          p.name.toLowerCase().includes(searchTerm) ||
          p.description?.toLowerCase().includes(searchTerm) ||
          p.category?.toLowerCase().includes(searchTerm)
        )
        .slice(0, maxPerCategory)
        .map(p => ({
          id: p.id,
          type: 'primitive' as SearchResultType,
          title: p.name,
          subtitle: p.category || 'Primitive',
          status: p.isPublished ? 'published' : 'draft',
          url: `/admin/primitives/${p.id}`,
        }));
      resultsByType.primitive = primitiveResults;
      allResults.push(...primitiveResults);
    }

    // Search executions
    if (includeTypes.includes('execution')) {
      const executionResults = executions
        .filter(e =>
          e.flowName.toLowerCase().includes(searchTerm) ||
          e.modemName?.toLowerCase().includes(searchTerm) ||
          e.locationName?.toLowerCase().includes(searchTerm) ||
          e.status.toLowerCase().includes(searchTerm)
        )
        .slice(0, maxPerCategory)
        .map(e => ({
          id: e.id,
          type: 'execution' as SearchResultType,
          title: e.flowName,
          subtitle: `${e.status} - ${e.modemName || 'Unknown modem'}`,
          status: e.status,
          url: `/executions/${e.id}`,
        }));
      resultsByType.execution = executionResults;
      allResults.push(...executionResults);
    }

    // Search devices
    if (includeTypes.includes('device')) {
      const deviceResults = devices
        .filter(d =>
          d.name.toLowerCase().includes(searchTerm) ||
          d.id.toLowerCase().includes(searchTerm)
        )
        .slice(0, maxPerCategory)
        .map(d => ({
          id: d.id,
          type: 'device' as SearchResultType,
          title: d.name,
          subtitle: `${d.status} - ${d.modems?.length || 0} modems`,
          status: d.status,
          url: `/admin/edge-devices?device=${d.id}`,
        }));
      resultsByType.device = deviceResults;
      allResults.push(...deviceResults);
    }

    // Search modems
    if (includeTypes.includes('modem')) {
      const modemResults: SearchResult[] = [];
      devices.forEach(device => {
        device.modems?.forEach(modem => {
          const imei = modem.imei || modem.id || 'unknown';
          if (
            modem.model?.toLowerCase().includes(searchTerm) ||
            imei.toLowerCase().includes(searchTerm) ||
            modem.manufacturer?.toLowerCase().includes(searchTerm)
          ) {
            modemResults.push({
              id: imei,
              type: 'modem' as SearchResultType,
              title: `${modem.manufacturer || ''} ${modem.model || 'Modem'}`.trim(),
              subtitle: `IMEI: ${imei} - ${modem.status}`,
              status: modem.status,
              url: `/admin/edge-devices?device=${device.id}&modem=${imei}`,
            });
          }
        });
      });
      resultsByType.modem = modemResults.slice(0, maxPerCategory);
      allResults.push(...modemResults.slice(0, maxPerCategory));
    }

    // Search locations
    if (includeTypes.includes('location')) {
      const locationResults = locations
        .filter(loc =>
          loc.name?.toLowerCase().includes(searchTerm) ||
          loc.displayName?.toLowerCase().includes(searchTerm)
        )
        .slice(0, maxPerCategory)
        .map(loc => ({
          id: loc.id,
          type: 'location' as SearchResultType,
          title: loc.displayName || loc.name,
          subtitle: `${loc.modemCount || 0} modems`,
          status: loc.status,
          url: `/locations/${loc.id}`,
        }));
      resultsByType.location = locationResults;
      allResults.push(...locationResults);
    }


    return { results: allResults, resultsByType };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, flows, primitives, executions, devices, locations, maxPerCategory, includeTypesKey]);

  return {
    query,
    setQuery,
    results: searchResults.results,
    resultsByType: searchResults.resultsByType,
    loading,
    error,
    totalCount: searchResults.results.length,
  };
}
